/**
 * Testimonials Carousel Component
 * Displays testimonials in a carousel format
 */

import styles from './TestimonialsCarousel.module.css';

export class TestimonialsCarousel {
  constructor(options = {}) {
    const { testimonials = null } = options;
    this.testimonials = testimonials || this.getDefaultTestimonials();
    this.currentIndex = 0;
  }

  getDefaultTestimonials() {
    return [
      {
        quote: "Classical Ballet of California has been an incredible experience. The instructors are patient, professional, and truly care about each student's growth. Ms. Baratova's expertise shines through in every class.",
        author: "Parent",
        role: "Student Parent",
        image: "/placeholder-testimonial.jpg"
      },
      {
        quote: "The Nutcracker performance was absolutely magical. With over 200 dancers from ages 3 to adult, it's amazing to see the entire community come together. The attention to detail and passion was evident in every moment.",
        author: "Audience Member",
        role: "Nutcracker Attendee",
        image: "/placeholder-testimonial.jpg"
      },
      {
        quote: "As an adult beginner, I was nervous about starting ballet. The welcoming environment and excellent instruction from experienced professionals like Ms. Baratova and Mr. Amato made it a joy to learn.",
        author: "Adult Student",
        role: "Adult Ballet Student",
        image: "/placeholder-testimonial.jpg"
      }
    ];
  }

  render() {
    const section = document.createElement('section');
    section.className = styles.testimonials;
    section.setAttribute('id', 'testimonials');

    const container = document.createElement('div');
    container.className = styles.container;

    // Header
    const header = document.createElement('div');
    header.className = styles.header;

    const title = document.createElement('h2');
    title.className = styles.title;
    title.textContent = 'What Our Community Says';
    header.appendChild(title);

    container.appendChild(header);

    // Carousel
    const carousel = document.createElement('div');
    carousel.className = styles.carousel;

    const track = document.createElement('div');
    track.className = styles.track;
    track.setAttribute('role', 'region');
    track.setAttribute('aria-label', 'Testimonials carousel');

    this.testimonials.forEach((testimonial, index) => {
      const slide = this.createSlide(testimonial, index);
      track.appendChild(slide);
    });

    carousel.appendChild(track);

    // Navigation
    const navigation = document.createElement('div');
    navigation.className = styles.navigation;

    const prevButton = document.createElement('button');
    prevButton.className = styles.navButton;
    prevButton.setAttribute('aria-label', 'Previous testimonial');
    prevButton.innerHTML = '‹';
    prevButton.addEventListener('click', () => this.goToSlide(this.currentIndex - 1));

    const nextButton = document.createElement('button');
    nextButton.className = styles.navButton;
    nextButton.setAttribute('aria-label', 'Next testimonial');
    nextButton.innerHTML = '›';
    nextButton.addEventListener('click', () => this.goToSlide(this.currentIndex + 1));

    navigation.appendChild(prevButton);
    navigation.appendChild(nextButton);

    // Dots
    const dots = document.createElement('div');
    dots.className = styles.dots;
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Testimonial indicators');

    this.testimonials.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = styles.dot;
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to testimonial ${index + 1}`);
      if (index === 0) {
        dot.classList.add(styles.active);
      }
      dot.addEventListener('click', () => this.goToSlide(index));
      dots.appendChild(dot);
    });

    container.appendChild(carousel);
    container.appendChild(navigation);
    container.appendChild(dots);
    section.appendChild(container);

    // Store references for carousel functionality
    this.track = track;
    this.dots = dots.querySelectorAll(`.${styles.dot}`);
    this.slides = track.querySelectorAll(`.${styles.slide}`);

    // Auto-advance (optional)
    this.startAutoAdvance();

    return section;
  }

  createSlide(testimonial, index) {
    const slide = document.createElement('div');
    slide.className = styles.slide;
    slide.setAttribute('data-index', index);
    if (index === 0) {
      slide.classList.add(styles.active);
    }

    const content = document.createElement('div');
    content.className = styles.content;

    // Quote
    const quote = document.createElement('blockquote');
    quote.className = styles.quote;
    quote.textContent = `"${testimonial.quote}"`;
    content.appendChild(quote);

    // Author
    const author = document.createElement('div');
    author.className = styles.author;

    if (testimonial.image) {
      const image = document.createElement('img');
      image.src = testimonial.image;
      image.alt = testimonial.author;
      image.className = styles.authorImage;
      author.appendChild(image);
    }

    const authorInfo = document.createElement('div');
    authorInfo.className = styles.authorInfo;

    const authorName = document.createElement('div');
    authorName.className = styles.authorName;
    authorName.textContent = testimonial.author;
    authorInfo.appendChild(authorName);

    if (testimonial.role) {
      const authorRole = document.createElement('div');
      authorRole.className = styles.authorRole;
      authorRole.textContent = testimonial.role;
      authorInfo.appendChild(authorRole);
    }

    author.appendChild(authorInfo);
    content.appendChild(author);

    slide.appendChild(content);
    return slide;
  }

  goToSlide(index) {
    // Handle wrap-around
    if (index < 0) {
      index = this.testimonials.length - 1;
    } else if (index >= this.testimonials.length) {
      index = 0;
    }

    this.currentIndex = index;

    // Update slides
    this.slides.forEach((slide, i) => {
      if (i === index) {
        slide.classList.add(styles.active);
      } else {
        slide.classList.remove(styles.active);
      }
    });

    // Update dots
    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add(styles.active);
      } else {
        dot.classList.remove(styles.active);
      }
    });

    // Update track position
    this.track.style.transform = `translateX(-${index * 100}%)`;
  }

  startAutoAdvance() {
    // Auto-advance every 5 seconds
    setInterval(() => {
      this.goToSlide(this.currentIndex + 1);
    }, 5000);
  }
}
